import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  closestCorners,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import FieldPalette from "../components/templateBuilder/FieldPalette";
import TemplateCanvas from "../components/templateBuilder/TemplateCanvas";
import FieldSettingsPanel from "../components/templateBuilder/FieldSettingsPanel";
import {
  createDefaultTemplate,
  createField,
  createSection,
  FIELD_TYPES,
  TEMPLATE_TYPES,
  hydrateTemplateFromApi,
  parseFieldDragId,
  parsePaletteDragId,
  parseSectionDragId,
  resolveTargetSectionId,
  toTemplatePayload,
} from "./templates/templateBuilderUtils";
import {
  createBuilderTemplate,
  createBuilderTemplateDraftVersion,
  getBuilderTemplate,
  listBuilderTemplateVersions,
} from "../api/templateBuilderApi";

export default function TemplateBuilderPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(createDefaultTemplate());
  const [selectedField, setSelectedField] = useState({ sectionId: "", fieldId: "" });
  const [loading, setLoading] = useState(id !== "new");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const importInputRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 6 } })
  );

  useEffect(() => {
    if (id === "new") {
      setTemplate(createDefaultTemplate());
      setSelectedField({ sectionId: "", fieldId: "" });
      setLoading(false);
      return;
    }

    async function load() {
      try {
        setLoading(true);
        setError("");
        const [templateData, versions] = await Promise.all([
          getBuilderTemplate(id),
          listBuilderTemplateVersions(id),
        ]);

        const draft = versions.find((item) => String(item?.status || "") === "draft");
        const published = versions.find((item) => String(item?.status || "") === "published");
        const chosen = draft || published || versions[0] || null;

        const hydrated = hydrateTemplateFromApi(templateData, chosen?.definition || { sections: [] });
        setTemplate(hydrated);
        setSelectedField({ sectionId: "", fieldId: "" });
      } catch (err) {
        setError(err?.response?.data?.message || err?.message || "Template laden mislukt");
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [id]);

  const selectedFieldObject = useMemo(() => {
    if (!selectedField.sectionId || !selectedField.fieldId) return null;
    const section = template.sections.find((item) => item.id === selectedField.sectionId);
    if (!section) return null;
    const field = section.fields.find((item) => item.id === selectedField.fieldId);
    return field || null;
  }, [template.sections, selectedField.sectionId, selectedField.fieldId]);

  function updateTemplateField(key, value) {
    setTemplate((prev) => ({ ...prev, [key]: value }));
  }

  function handleAddSection() {
    setTemplate((prev) => ({
      ...prev,
      sections: [...prev.sections, createSection(`Sectie ${prev.sections.length + 1}`)],
    }));
  }

  function handleRemoveSection(sectionId) {
    setTemplate((prev) => ({
      ...prev,
      sections: prev.sections.filter((section) => section.id !== sectionId),
    }));

    if (selectedField.sectionId === sectionId) {
      setSelectedField({ sectionId: "", fieldId: "" });
    }
  }

  function handleUpdateSectionTitle(sectionId, title) {
    setTemplate((prev) => ({
      ...prev,
      sections: prev.sections.map((section) =>
        section.id === sectionId ? { ...section, title } : section
      ),
    }));
  }

  function handleSelectField(sectionId, fieldId) {
    setSelectedField({ sectionId, fieldId });
  }

  function handleSelectSection() {
    setSelectedField({ sectionId: "", fieldId: "" });
  }

  function handleChangeSelectedField(key, value) {
    if (!selectedField.sectionId || !selectedField.fieldId) return;
    setTemplate((prev) => ({
      ...prev,
      sections: prev.sections.map((section) => {
        if (section.id !== selectedField.sectionId) return section;
        return {
          ...section,
          fields: section.fields.map((field) =>
            field.id === selectedField.fieldId ? { ...field, [key]: value } : field
          ),
        };
      }),
    }));
  }

  function handleDeleteSelectedField() {
    if (!selectedField.sectionId || !selectedField.fieldId) return;
    setTemplate((prev) => ({
      ...prev,
      sections: prev.sections.map((section) => {
        if (section.id !== selectedField.sectionId) return section;
        return {
          ...section,
          fields: section.fields.filter((field) => field.id !== selectedField.fieldId),
        };
      }),
    }));
    setSelectedField({ sectionId: "", fieldId: "" });
  }

  function handleDragEnd(event) {
    const activeId = String(event.active?.id || "");
    const overId = String(event.over?.id || "");
    if (!activeId || !overId) return;

    const activePalette = parsePaletteDragId(activeId);
    const activeSection = parseSectionDragId(activeId);
    const activeField = parseFieldDragId(activeId);

    if (activePalette) {
      const targetSectionId = resolveTargetSectionId(overId);
      if (!targetSectionId) return;
      const newField = createField(activePalette.fieldType);

      setTemplate((prev) => ({
        ...prev,
        sections: prev.sections.map((section) =>
          section.id === targetSectionId
            ? { ...section, fields: [...section.fields, newField] }
            : section
        ),
      }));
      return;
    }

    if (activeSection) {
      const overSection = parseSectionDragId(overId);
      if (!overSection) return;

      setTemplate((prev) => {
        const fromIndex = prev.sections.findIndex((section) => section.id === activeSection.sectionId);
        const toIndex = prev.sections.findIndex((section) => section.id === overSection.sectionId);
        if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return prev;
        return {
          ...prev,
          sections: arrayMove(prev.sections, fromIndex, toIndex),
        };
      });
      return;
    }

    if (activeField) {
      const overField = parseFieldDragId(overId);
      const targetSectionId = resolveTargetSectionId(overId);
      if (!targetSectionId) return;

      setTemplate((prev) => {
        const next = { ...prev, sections: prev.sections.map((section) => ({ ...section, fields: [...section.fields] })) };

        const sourceSection = next.sections.find((section) => section.id === activeField.sectionId);
        if (!sourceSection) return prev;
        const sourceIndex = sourceSection.fields.findIndex((field) => field.id === activeField.fieldId);
        if (sourceIndex < 0) return prev;

        const [movedField] = sourceSection.fields.splice(sourceIndex, 1);

        const destSection = next.sections.find((section) => section.id === targetSectionId);
        if (!destSection) return prev;

        if (overField && overField.sectionId === targetSectionId) {
          const overIndex = destSection.fields.findIndex((field) => field.id === overField.fieldId);
          if (overIndex >= 0) {
            destSection.fields.splice(overIndex, 0, movedField);
          } else {
            destSection.fields.push(movedField);
          }
        } else {
          destSection.fields.push(movedField);
        }

        return next;
      });
    }
  }

  async function handleSaveDraft() {
    const payload = toTemplatePayload(template);

    if (!payload.name) {
      setError("Naam is verplicht");
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      if (!template.id || id === "new") {
        const created = await createBuilderTemplate(payload);
        setSuccess("Template draft opgeslagen");
        navigate(`/template-builder/${created?._id || created?.id || "new"}`, { replace: true });
        return;
      }

      await createBuilderTemplateDraftVersion(template.id, {
        sections: payload.sections,
      });
      setSuccess("Nieuwe draft versie opgeslagen");
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  }

  function handleImportClick() {
    importInputRef.current?.click();
  }

  function handleExportJson() {
    const payload = toTemplatePayload(template);
    const exportData = {
      name: payload.name,
      description: payload.description,
      type: payload.type,
      category: payload.category,
      status: "draft",
      sections: payload.sections,
      definition: { sections: payload.sections },
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: "application/json;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    const safeName = String(payload.name || "template")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "template";
    anchor.href = url;
    anchor.download = `${safeName}.json`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }

  async function handleImportFile(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    try {
      const raw = await file.text();
      const parsed = JSON.parse(raw);
      const imported = normalizeImportedTemplate(parsed);
      setTemplate(imported);
      setSelectedField({ sectionId: "", fieldId: "" });
      setSuccess("Template geïmporteerd. Controleer en sla op als draft.");
      setError("");
    } catch (err) {
      setError(err?.message || "Template import mislukt");
      setSuccess("");
    }
  }

  return (
    <div className="mx-auto max-w-[1700px] space-y-4 p-3 md:p-5">
      <section className="rounded-xl border bg-white p-4 shadow-sm">
        <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_220px_180px]">
          <input
            value={template.name}
            onChange={(event) => updateTemplateField("name", event.target.value)}
            placeholder="Template naam"
            className="rounded border border-gray-300 px-3 py-2 text-sm"
          />

          <select
            value={template.type}
            onChange={(event) => updateTemplateField("type", event.target.value)}
            className="rounded border border-gray-300 px-3 py-2 text-sm"
          >
            {TEMPLATE_TYPES.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleImportClick}
              className="rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 hover:bg-gray-50"
            >
              Import JSON
            </button>
            <button
              type="button"
              onClick={handleExportJson}
              className="rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 hover:bg-gray-50"
            >
              Export JSON
            </button>
            <input
              ref={importInputRef}
              type="file"
              accept="application/json,.json"
              className="hidden"
              onChange={handleImportFile}
            />
            <button
              type="button"
              onClick={handleSaveDraft}
              disabled={saving || loading}
              className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {saving ? "Opslaan..." : "Save draft"}
            </button>
          </div>
        </div>

        <textarea
          value={template.description}
          onChange={(event) => updateTemplateField("description", event.target.value)}
          placeholder="Beschrijving"
          className="mt-3 min-h-[80px] w-full rounded border border-gray-300 px-3 py-2 text-sm"
        />

        {loading && <p className="mt-2 text-sm text-gray-500">Template laden...</p>}
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        {success && <p className="mt-2 text-sm text-emerald-700">{success}</p>}
      </section>

      <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={handleDragEnd}>
        <div className="grid gap-4 xl:grid-cols-[260px_minmax(0,1fr)_340px]">
          <FieldPalette fieldTypes={FIELD_TYPES} />

          <TemplateCanvas
            sections={template.sections}
            selectedField={selectedField}
            onSelectField={handleSelectField}
            onSelectSection={handleSelectSection}
            onUpdateSectionTitle={handleUpdateSectionTitle}
            onRemoveSection={handleRemoveSection}
            onAddSection={handleAddSection}
          />

          <FieldSettingsPanel
            field={selectedFieldObject}
            onChangeField={handleChangeSelectedField}
            onDeleteField={handleDeleteSelectedField}
          />
        </div>
      </DndContext>
    </div>
  );
}

function normalizeImportedTemplate(input) {
  const base = createDefaultTemplate();
  const source = input && typeof input === "object" ? input : {};
  const definitionSections = Array.isArray(source?.definition?.sections)
    ? source.definition.sections
    : null;
  const rawSections = Array.isArray(source?.sections)
    ? source.sections
    : definitionSections || [];

  const sections =
    rawSections.length > 0
      ? rawSections.map((section, sectionIndex) => ({
          id: String(section?.id || `sec_import_${sectionIndex + 1}`),
          title: String(section?.title || `Sectie ${sectionIndex + 1}`),
          repeatable: Boolean(section?.repeatable),
          fields: Array.isArray(section?.fields)
            ? section.fields.map((field, fieldIndex) => ({
                id: String(field?.id || `fld_import_${sectionIndex + 1}_${fieldIndex + 1}`),
                type: String(field?.type || "text").toLowerCase(),
                label: String(field?.label || "Veld"),
                required: Boolean(field?.required),
                placeholder: String(field?.placeholder || ""),
                options: Array.isArray(field?.options)
                  ? field.options.map((item) => String(item || "")).filter(Boolean)
                  : [],
                multiple: Boolean(field?.multiple),
              }))
            : [],
        }))
      : base.sections;

  return {
    ...base,
    id: "",
    name: String(source?.name || ""),
    description: String(source?.description || ""),
    type: String(source?.type || base.type),
    category: String(source?.category || base.category),
    status: "draft",
    sections,
  };
}
